import {
  IAreasTransfers,
  ISharingBasisEnum,
} from "commonFnBcode/types/dmc.types";
import React from "react";
import {
  Table,
  Badge,
  Button,
  Card,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

interface AreaTransfer {
  id: number;
  from?: { name: string };
  to?: { name: string };
  description?: string;
  transferType: "Pickup" | "Drop" | "Pickup & Drop";
  sharingBasis: "PVT" | "SIC";
  amount: number;
  isStandAlone: boolean;
  isPerPax: boolean;
  minPax?: number;
  maxPax?: number;
  validFromDate: string;
  validTillDate: string;
}

interface AreaTransferTableProps {
  areas: IAreasTransfers[] | undefined;
  onDelete: (id: any) => void;
  onCopy?: (area: IAreasTransfers) => void;
}

const AreaTransferTable: React.FC<AreaTransferTableProps> = ({
  areas,
  onDelete,
  onCopy,
}) => {
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getTransferBadge = (type: string): string => {
    switch (type) {
      case "Pickup":
        return "success";
      case "Drop":
        return "info";
      case "Pickup & Drop":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getSharingBadge = (basis: string): string => {
    return basis === "PVT" ? "primary" : "dark";
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white py-3">
        <h5 className="mb-0">Area Transfers</h5>
      </Card.Header>
      <div className="table-responsive">
        <Table hover className="align-middle mb-0">
          <thead className="bg-light">
            <tr>
              <th className="fw-semibold">ID</th>
              <th className="fw-semibold">From</th>
              <th className="fw-semibold">To</th>
              <th className="fw-semibold">Trasnfer & Sharing</th>
              <th className="fw-semibold">Amount</th>
              <th className="fw-semibold">Start Date</th>
              <th className="fw-semibold">End Date</th>
              <th className="fw-semibold text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {areas?.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No area transfers found
                </td>
              </tr>
            ) : (
              areas?.map((area) => {
                const style = {
                  ...(area.isStandAlone
                    ? { backgroundColor: "rgba(225,100,100,0.2)" }
                    : {}),
                };
                return (
                  <tr key={area.id} style={style}>
                    <td style={style}>
                      {" "}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {area.description || "No description available"}
                          </Tooltip>
                        }
                      >
                        <i
                          className="fa fa-info-circle text-muted"
                          role="button"
                        ></i>
                      </OverlayTrigger>{" "}
                      {area.id}
                    </td>
                    <td style={style}>{area.from?.name || "N/A"}</td>
                    <td style={style}>{area.to?.name || "N/A"}</td>
                    <td style={style}>
                      <div className="d-flex gap-1">
                        <Badge bg={getTransferBadge(area.transferType)}>
                          {area.transferType}
                        </Badge>
                        <Badge bg={getSharingBadge(area.sharingBasis)}>
                          {area.sharingBasis}
                        </Badge>
                        {area.sharingBasis === ISharingBasisEnum.private && (
                          <Badge bg="success">
                            {area.minPax && area.maxPax
                              ? ` min: ${area.minPax}, max: ${area.maxPax}`
                              : ""}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td style={style} className="fw-semibold">
                      ${area.amount.toFixed(2)}
                    </td>
                    <td style={style}>
                      <div className="small">
                        <div>{formatDate(area.validFromDate as any)}</div>
                      </div>
                    </td>
                    <td style={style}>
                      <div className="small">
                        <div>{formatDate(area.validTillDate as any)}</div>
                      </div>
                    </td>
                    <td style={style}>
                      <div className="d-flex justify-content-center gap-2">
                        {onCopy && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onCopy(area)}
                            className="btn-icon rounded-1"
                          >
                            <i className="fa fa-copy"></i>
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDelete(area)}
                          className="btn-flat"
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default AreaTransferTable;
